import {
  LayoutDashboard,
  UsersRound,
  Users,
  FolderKanban,
  Network,
  ShieldCheck,
  Activity,
  type LucideIcon,
} from 'lucide-react';

export type OrgNavGroup = 'organization' | 'people' | 'workspaces' | 'reseller';

export interface OrgNavEntry {
  slug: string;
  label: string;
  icon: LucideIcon;
  group: OrgNavGroup;
  /** When true, only shown to resellers (`is_reseller === true`). */
  resellerOnly?: boolean;
  /**
   * Hidden from the sidebar but still route-matchable (e.g. detail pages that
   * live under a visible slug like teams/:id under teams).
   */
  hidden?: boolean;
}

/**
 * Organizations console sidebar entries, grouped for display.
 * Reseller-only entries are gated in OrganizationsConsole via `useOrgMe().is_reseller`.
 * `orgActiveSlug()` uses longest-prefix matching so detail pages (teams/:id,
 * projects/:id/resources, etc.) correctly highlight their parent entry.
 */
export const ORG_NAV: OrgNavEntry[] = [
  { slug: 'overview',       label: 'Overview',       icon: LayoutDashboard, group: 'organization' },
  { slug: 'activity',       label: 'Activity',        icon: Activity,        group: 'organization' },
  { slug: 'members',        label: 'Members',         icon: UsersRound,      group: 'people' },
  { slug: 'teams',          label: 'Teams',           icon: Users,           group: 'workspaces' },
  { slug: 'projects',       label: 'Projects',        icon: FolderKanban,    group: 'workspaces' },
  { slug: 'sub-customers',  label: 'Customers',       icon: Network,         group: 'reseller', resellerOnly: true },
  { slug: 'admins',         label: 'Admins',          icon: ShieldCheck,     group: 'reseller', resellerOnly: true },
];

const GROUP_HEADING: Record<OrgNavGroup, string> = {
  organization: 'Organization',
  people:       'People',
  workspaces:   'Workspaces',
  reseller:     'Reseller',
};

export interface OrgNavSection {
  heading: string;
  entries: OrgNavEntry[];
}

/**
 * Build ordered sections from ORG_NAV, optionally excluding the reseller group.
 * Only visible (non-hidden) entries are included.
 */
export function buildOrgNavSections(isReseller: boolean): OrgNavSection[] {
  const order: OrgNavGroup[] = ['organization', 'people', 'workspaces', 'reseller'];
  const grouped = new Map<OrgNavGroup, OrgNavEntry[]>();
  for (const entry of ORG_NAV) {
    if (entry.hidden) continue;
    if (entry.resellerOnly && !isReseller) continue;
    if (!grouped.has(entry.group)) grouped.set(entry.group, []);
    grouped.get(entry.group)!.push(entry);
  }
  return order
    .filter((g) => grouped.has(g))
    .map((g) => ({ heading: GROUP_HEADING[g], entries: grouped.get(g)! }));
}

/** Longest nav slug that prefixes the sub-path → active sidebar entry. */
export function orgActiveSlug(subPath: string): string {
  const slugs = ORG_NAV.map((e) => e.slug).filter(Boolean);
  let best = '';
  for (const slug of slugs) {
    if ((subPath === slug || subPath.startsWith(`${slug}/`)) && slug.length > best.length) {
      best = slug;
    }
  }
  return best;
}
